import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateUsername = (username) => {
    if (!username) return 'Username is required';
    if (username?.length < 3) return 'Username must be at least 3 characters';
    if (username?.length > 20) return 'Username must be less than 20 characters';
    if (!/^[a-zA-Z0-9_]+$/?.test(username)) return 'Username can only contain letters, numbers, and underscores';
    return '';
  };

  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex?.test(email)) return 'Invalid email format';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password?.length < 8) return 'Password must be at least 8 characters';
    if (!/(?=.*[a-z])/?.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/(?=.*[A-Z])/?.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/(?=.*\d)/?.test(password)) return 'Password must contain at least one number';
    if (!/(?=.*[@$!%*?&])/?.test(password)) return 'Password must contain at least one special character (@$!%*?&)';
    return '';
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) return 'Please confirm your password';
    if (confirmPassword !== password) return 'Passwords do not match';
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const newErrors = {};
    newErrors.username = validateUsername(formData?.username);
    newErrors.displayName = !formData?.displayName ? 'Display name is required' : '';
    newErrors.email = validateEmail(formData?.email);
    newErrors.password = validatePassword(formData?.password);
    newErrors.confirmPassword = validateConfirmPassword(formData?.confirmPassword, formData?.password);
    newErrors.agreeToTerms = !formData?.agreeToTerms ? 'You must agree to the terms and conditions' : '';

    const hasErrors = Object.values(newErrors)?.some(error => error !== '');

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    const result = await register(
      formData?.username,
      formData?.email,
      formData?.password,
      formData?.displayName
    );

    if (result?.success) {
      navigate('/main-menu');
    } else {
      setErrors({
        submit: result?.error || 'Registration failed. Please try again.'
      });
      setIsLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (/(?=.*[a-z])/?.test(password)) strength++;
    if (/(?=.*[A-Z])/?.test(password)) strength++;
    if (/(?=.*\d)/?.test(password)) strength++;
    if (/(?=.*[@$!%*?&])/?.test(password)) strength++;

    if (strength <= 2) return { strength: 33, label: 'WEAK', color: 'bg-error' };
    if (strength <= 3) return { strength: 66, label: 'MEDIUM', color: 'bg-warning' };
    return { strength: 100, label: 'STRONG', color: 'bg-success' };
  };

  const passwordStrength = getPasswordStrength(formData?.password);

  return (
    <form onSubmit={handleSubmit} className="space-y-base">
      {errors?.submit && (
        <div className="p-3 md:p-4 bg-error/10 border-2 border-error rounded-sm">
          <div className="flex items-start gap-2 md:gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
            <p className="text-xs md:text-sm font-caption text-error leading-relaxed">
              {errors?.submit}
            </p>
          </div>
        </div>
      )}
      <div className="space-y-sm">
        <Input
          label="Username"
          type="text"
          name="username"
          placeholder="Enter your username"
          value={formData?.username}
          onChange={handleInputChange}
          error={errors?.username}
          required
          description="3-20 characters, letters, numbers, and underscores only"
        />

        <Input
          label="Display Name"
          type="text"
          name="displayName"
          placeholder="Enter your display name"
          value={formData?.displayName}
          onChange={handleInputChange}
          error={errors?.displayName}
          required
          description="This is how other players will see you"
        />

        <Input
          label="Email Address"
          type="email"
          name="email"
          placeholder="Enter your email"
          value={formData?.email}
          onChange={handleInputChange}
          error={errors?.email}
          required
          description="We'll never share your email with anyone"
        />

        <div className="relative">
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Create a strong password"
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
            description="Min 8 characters with uppercase, lowercase, number & special character"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 p-1 text-muted-foreground hover:text-primary transition-colors duration-base"
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
        </div>

        {formData?.password && (
          <div className="space-y-xs">
            <div className="flex items-center justify-between text-xs font-caption">
              <span className="text-muted-foreground">Password Strength:</span>
              <span className={`font-bold ${
                passwordStrength?.label === 'WEAK' ? 'text-error' :
                passwordStrength?.label === 'MEDIUM'? 'text-warning' : 'text-success'
              }`}>
                {passwordStrength?.label}
              </span>
            </div>
            <div className="h-2 bg-muted rounded-sm overflow-hidden border border-primary/30">
              <div
                className={`h-full transition-all duration-300 ${passwordStrength?.color}`}
                style={{ width: `${passwordStrength?.strength}%` }}
              />
            </div>
          </div>
        )}

        <div className="relative">
          <Input
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            placeholder="Re-enter your password"
            value={formData?.confirmPassword}
            onChange={handleInputChange}
            error={errors?.confirmPassword}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-9 p-1 text-muted-foreground hover:text-primary transition-colors duration-base"
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
        </div>
      </div>
      <div className="pt-sm">
        <Checkbox
          name="agreeToTerms"
          checked={formData?.agreeToTerms}
          onChange={handleInputChange}
          error={errors?.agreeToTerms}
          label={
            <span className="text-sm font-caption">
              I agree to the{' '}
              <button
                type="button"
                className="text-primary hover:text-secondary transition-colors duration-base underline"
                onClick={(e) => {
                  e?.preventDefault();
                }}
              >
                Terms of Service
              </button>
              {' '}and{' '}
              <button
                type="button"
                className="text-primary hover:text-secondary transition-colors duration-base underline"
                onClick={(e) => {
                  e?.preventDefault();
                }}
              >
                Privacy Policy
              </button>
            </span>
          }
        />
      </div>
      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        iconName="UserPlus"
        iconPosition="left"
        className="mt-lg"
      >
        {isLoading ? 'Creating Account...' : 'Create Account'}
      </Button>
    </form>
  );
};

export default RegistrationForm;